export function Modules() {
  return (
    <div className="w-full max-w-7xl mx-auto my-20 px-6">
      {/* Section Header */}
      <div className="text-center mb-20">
        <h2 className="text-4xl md:text-5xl font-extrabold text-black mb-6 tracking-tight">
          Integration & Development Services
        </h2>
        <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          A comprehensive suite of solutions designed to seamlessly connect your
          systems, enhance workflows, and accelerate your digital transformation journey.
        </p>
      </div>

      {/* Modules Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {modules.map((module, i) => (
          <div
            key={i}
            className="relative bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-gray-200 overflow-hidden group"
          >
            {/* Gradient Accent Top Bar */}
            <div className={`h-1.5 ${module.gradient}`}></div>

            <div className="p-8">
              {/* Icon */}
              <div className="relative mb-6">
                <div
                  className={`w-16 h-16 ${module.iconBg} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                >
                  <span className="text-white text-2xl">{module.icon}</span>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors">
                {module.title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 leading-relaxed mb-6 text-sm md:text-base">
                {module.desc}
              </p>

              {/* Features */}
              <ul className="space-y-3">
                {module.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-gray-700">
                    <span className="flex items-center justify-center w-6 h-6 bg-green-100 rounded-full mr-3">
                      <span className="text-green-500 text-xs">✓</span>
                    </span>
                    <span className="text-sm md:text-base">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Badge */}
              <div className="mt-6">
                <span
                  className={`text-xs font-semibold px-3 py-1 rounded-full border ${module.badgeColor}`}
                >
                  {module.type}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Capabilities */}
      <div className="mt-20 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-10 border border-blue-100 shadow-sm">
        <h3 className="text-3xl font-bold text-center mb-12 text-gray-900">
          Our Technology Stack & Expertise
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {techStack.map((stack, i) => (
            <div
              key={i}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition duration-300"
            >
              <div className="text-3xl mb-3">{stack.icon}</div>
              <h4 className="font-bold text-gray-900 mb-1 text-base">
                {stack.title}
              </h4>
              <p className="text-gray-600 text-sm">{stack.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const modules = [
  {
    title: "ERP System Integration",
    desc: "Seamlessly connect your ERP with third-party systems, legacy applications, and modern platforms for unified data flow and automation.",
    icon: "🔗",
    iconBg: "bg-white border border-gray-200",
    gradient: "bg-gradient-to-r from-blue-500 to-blue-600",
    badgeColor: "border-blue-300 text-blue-700 bg-blue-50",
    type: "Integration",
    features: ["Third-Party Integration", "Legacy System Connectivity", "Real-time Sync"],
  },
  {
    title: "Financial Integration",
    desc: "Integrate payment systems including M-Pesa, bank APIs, fintech platforms, and gateways for seamless financial operations.",
    icon: "💰",
    iconBg: "bg-gradient-to-br from-green-500 to-green-600",
    gradient: "bg-gradient-to-r from-green-500 to-green-600",
    badgeColor: "border-green-300 text-green-700 bg-green-50",
    type: "Integration",
    features: ["M-Pesa Integration", "Bank API Connectivity", "Payment Gateway Setup"],
  },
  {
    title: "Hardware & IoT Integration",
    desc: "Connect weigh bridges, barcode scanners, POS systems, and IoT devices to central systems for automated data capture.",
    icon: "⚙️",
    iconBg: "bg-gradient-to-br from-purple-500 to-purple-600",
    gradient: "bg-gradient-to-r from-purple-500 to-purple-600",
    badgeColor: "border-purple-300 text-purple-700 bg-purple-50",
    type: "Integration",
    features: ["Weigh Bridge Systems", "Barcode Scanners", "IoT Devices"],
  },
  {
    title: "Custom Mobile Development",
    desc: "Build native and cross-platform mobile apps with modern frameworks, AI capabilities, and seamless backend integration.",
    icon: "📱",
    iconBg: "bg-gradient-to-br from-orange-500 to-orange-600",
    gradient: "bg-gradient-to-r from-orange-500 to-orange-600",
    badgeColor: "border-orange-300 text-orange-700 bg-orange-50",
    type: "Development",
    features: ["iOS & Android Apps", "Cross-Platform Solutions", "AI-Powered Features"],
  },
  {
    title: "Web Application Development",
    desc: "Create responsive web apps with modern frameworks, cloud deployment, and enterprise-grade security.",
    icon: "🌐",
    iconBg: "bg-gradient-to-br from-red-500 to-red-600",
    gradient: "bg-gradient-to-r from-red-500 to-red-600",
    badgeColor: "border-red-300 text-red-700 bg-red-50",
    type: "Development",
    features: ["Progressive Web Apps", "Cloud Deployment", "Enterprise Security"],
  },
  {
    title: "AI & Automation Solutions",
    desc: "Leverage AI, machine learning, and robotic process automation to optimize business processes and decisions.",
    icon: "🤖",
    iconBg: "bg-gradient-to-br from-indigo-500 to-indigo-600",
    gradient: "bg-gradient-to-r from-indigo-500 to-indigo-600",
    badgeColor: "border-indigo-300 text-indigo-700 bg-indigo-50",
    type: "Development",
    features: ["Machine Learning", "Process Automation", "Predictive Analytics"],
  },
];

const techStack = [
  { icon: "🔗", title: "API Integration", desc: "REST, SOAP, GraphQL" },
  { icon: "🤖", title: "AI & Automation", desc: "Machine Learning, RPA" },
  { icon: "☁️", title: "Cloud Platforms", desc: "AWS, Azure, Google Cloud" },
  { icon: "📱", title: "Mobile & Web", desc: "React, Flutter, Native" },
];
